import { useCallback, useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import { FeedbackFlow, type FeedbackFeature } from '../feedback/FeedbackFlow';
import { VoiceIntro } from '../feedback/voice/VoiceIntro';
import { AppFooter } from './AppFooter';
import { AppHeader } from './AppHeader';
import { FeedbackOpenerProvider } from './feedbackOpener';
import { HelpPanel, type HelpContact, type HelpPanelProps } from './HelpPanel';
import { useHeroPreference } from './Hero';
import { LEVEL_ONE } from './levelOne';
import { PreviewBanner } from './PreviewBanner';
import { returnFocus } from './returnFocus';
import { SetupSandboxBanner } from './SetupSandboxBanner';
import { SearchPalette } from './SearchPalette';
import { clearHelpShown, findHelpAnchor, showHelpAnchor } from './showMe';
import { useRouteFocus } from './useRouteFocus';
import type {
  BellState,
  HelpAnchorRef,
  HelpSource,
  HelpTopic,
  LevelOneId,
  Me,
  PageHelp,
  PreviewState,
  RecentFn,
  SearchFn,
  SearchResult,
} from './types';
import { cx } from '../ui/cx';
import s from './AppShell.module.css';

export interface AppShellProps {
  me: Me;
  /** Level 1 items this person can use (shown in the fixed order). */
  nav: readonly LevelOneId[];
  navHrefs?: Partial<Record<LevelOneId, string>>;
  /**
   * The Level 1 item the page belongs to (QA-045), from its route. Undefined: the item whose
   * address the page is under.
   */
  current?: LevelOneId | null;
  /** The bell: unread notices and getting-started steps. */
  bell: BellState;
  /** Shows "Preview as" in the avatar menu. */
  canPreview: boolean;
  /** An active preview session shows the gold banner above the header. */
  preview?: PreviewState | null;
  onExitPreview?: () => void;
  /**
   * Opens Preview as (TEST1), from the avatar menu or the banner's Change. `returnFocusTo` is the
   * control that opened it, for focus to come back to.
   */
  onPreviewOpen?: (returnFocusTo: HTMLElement | null) => void;
  /** Admin › AI › Help chat. Off: Help shows search and topics only. */
  aiHelpEnabled: boolean;
  /** Help for the current page (routes provide it through `handle.help`). */
  help: PageHelp;
  helpTopics: readonly HelpTopic[];
  /** Where the topics come from: loading state, popular ones, server search (APP-048). */
  helpSource?: HelpSource;
  helpChat?: HelpPanelProps['chat'];
  helpContact?: HelpContact;
  /** Help was opened: the layout loads its topics then, not on every page. */
  onHelpOpen?: () => void;
  search: SearchFn;
  /** Before anything is typed: shortcuts for the person's roles (SHELL1). */
  searchJumpTo?: readonly SearchResult[];
  /** Before anything is typed: what the person opened lately; left out while previewing. */
  searchRecent?: RecentFn;
  onSignOut: () => void;
  footerPhotoStrip?: boolean;
  settingsHref?: string;
  /** Where the logo and the bell's "Open Home" go (Payroll's Home is its queue). */
  homeHref?: string;
  awardsPolicyHref?: string | null;
  /**
   * What Send feedback offers this person (APP-104). Left out, the header button and the menu and
   * Help entries are hidden, the way Level 1 items are.
   */
  feedback?: FeedbackFeature | null;
  /** The server said feedback is off (the setting changed): the app refreshes what it offers. */
  onFeedbackOff?: () => void;
  /** Send feedback found the session ended (401): the app takes the person to sign-in. */
  onFeedbackSessionEnded?: () => void;
  /** A voice recording ended: the day's voice time changed (APP-104 §3.2). */
  onFeedbackVoiceUsed?: () => void;
  /** The person went on from the voice introduction (APP-104 §3.2a): it isn't shown again. */
  onFeedbackVoiceIntroSeen?: () => void;
  /** A piece of feedback was sent: My feedback and its Settings entry now have it (APP-138). */
  onFeedbackSent?: () => void;
  children: ReactNode;
}

/** Where the Help panel covers the page instead of sitting beside it (HelpPanel.module.css). */
const HELP_OVERLAYS = '(max-width: 960px)';

function isPaletteShortcut(event: KeyboardEvent): boolean {
  return event.key.toLowerCase() === 'k' && (event.ctrlKey || event.metaKey) && !event.altKey;
}

/**
 * The app shell: optional preview banner, header, the page (`<main>`) with the Help side panel
 * beside it, and the footer pinned to the bottom of the window. Ctrl K / ⌘K opens Search & jump.
 */
export function AppShell({
  me,
  nav,
  navHrefs,
  current,
  bell,
  canPreview,
  preview = null,
  onExitPreview,
  onPreviewOpen,
  aiHelpEnabled,
  help,
  helpTopics,
  helpSource,
  helpChat,
  helpContact,
  onHelpOpen,
  search,
  searchJumpTo,
  searchRecent,
  onSignOut,
  footerPhotoStrip = false,
  settingsHref,
  homeHref,
  awardsPolicyHref,
  feedback = null,
  onFeedbackOff,
  onFeedbackSessionEnded,
  onFeedbackVoiceUsed,
  onFeedbackVoiceIntroSeen,
  onFeedbackSent,
  children,
}: AppShellProps): ReactElement {
  // Each move to another page puts focus on its heading, so screen readers announce it (QA-022).
  useRouteFocus('main');
  const { pathname } = useLocation();
  const [helpOpen, setHelpOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [shownNote, setShownNote] = useState('');
  const helpButtonRef = useRef<HTMLButtonElement>(null);
  // Send feedback (APP-104): one flow for the header button, the account menu and Help.
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  // APP-104 §3.2a: the first time a click would start recording, the voice introduction comes
  // first; the flow mounts (and the microphone starts) only once the person goes on from it.
  const [feedbackIntro, setFeedbackIntro] = useState(false);
  // Type instead, chosen in the introduction: the flow opens on the review.
  const [feedbackTyping, setFeedbackTyping] = useState(false);
  // The offer the server refused: hidden until the app is given a different one.
  const [refusedOffer, setRefusedOffer] = useState<FeedbackFeature | null>(null);
  const feedbackReturn = useRef<HTMLElement | null>(null);
  const feedbackOpenRef = useRef(false);
  useEffect(() => {
    feedbackOpenRef.current = feedbackOpen || feedbackIntro;
  }, [feedbackOpen, feedbackIntro]);
  const hero = useHeroPreference().variant;
  const feedbackOn = feedback !== null && feedback !== refusedOffer;
  // Only a click that would record: with voice off or today's time used up, the review opens.
  const introFirst =
    feedback?.voiceIntro === true && feedback.voice && feedback.voiceSecondsLeft !== 0;

  // A "Show me" highlight belongs to the page it was shown on.
  useEffect(() => clearHelpShown, [pathname]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (!isPaletteShortcut(event)) return;
      // Behind the feedback dialog the palette would open under it.
      if (feedbackOpenRef.current) return;
      event.preventDefault();
      setSearchOpen(true);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  // Decorative loops pause while the tab is in the background.
  useEffect(() => {
    const sync = (): void => {
      document.documentElement.dataset['pageHidden'] = String(document.hidden);
    };
    document.addEventListener('visibilitychange', sync);
    return () => {
      document.removeEventListener('visibilitychange', sync);
    };
  }, []);

  // QA-060: Help can be opened from the header or from the footer. Closing it, or Feedback opened
  // from it, puts focus back on the button that opened it, without moving the page unless that
  // button is outside the window (returnFocus).
  const helpOpener = useRef<HTMLElement | null>(null);
  const helpReturn = useCallback((): HTMLElement | null => {
    const opener = helpOpener.current;
    return opener?.isConnected === true ? opener : helpButtonRef.current;
  }, []);

  // Back to whatever opened Help most recently; closed from another control (the header's toggle),
  // focus goes to that control.
  const closeHelp = useCallback(
    (closer?: HTMLElement | null) => {
      setHelpOpen(false);
      const target = closer ?? helpReturn();
      if (target) returnFocus(target);
    },
    [helpReturn],
  );

  const openHelp = useCallback(
    (opener?: HTMLElement) => {
      helpOpener.current = opener ?? null;
      setHelpOpen(true);
      onHelpOpen?.();
    },
    [onHelpOpen],
  );

  // Help's "Show me" (APP-090): only something on this page the person can see. Where the panel
  // covers the page it closes first (focus goes to the thing, not back to the Help button), and
  // the status line below says what was highlighted, wherever focus is.
  const showMe = useCallback((anchor: HelpAnchorRef): boolean => {
    if (findHelpAnchor(anchor.id) === null) return false;
    if (typeof window.matchMedia === 'function' && window.matchMedia(HELP_OVERLAYS).matches)
      setHelpOpen(false);
    if (!showHelpAnchor(anchor.id)) return false;
    // Emptied first, so showing the same thing twice is announced twice.
    setShownNote('');
    window.requestAnimationFrame(() => {
      setShownNote(`Highlighted on the page: ${anchor.label}.`);
    });
    return true;
  }, []);

  // The Help panel closes first, so the picture is of the page, not of Help.
  const openFeedback = useCallback(
    (from: HTMLElement | null) => {
      feedbackReturn.current = from;
      setHelpOpen(false);
      setFeedbackTyping(false);
      if (introFirst) setFeedbackIntro(true);
      else setFeedbackOpen(true);
    },
    [introFirst],
  );
  // Start recording or Type instead: the flow takes over, and the introduction is done with.
  const leaveIntro = useCallback(
    (typing: boolean) => {
      onFeedbackVoiceIntroSeen?.();
      setFeedbackIntro(false);
      setFeedbackTyping(typing);
      setFeedbackOpen(true);
    },
    [onFeedbackVoiceIntroSeen],
  );
  const closeFeedback = useCallback(() => {
    setFeedbackOpen(false);
    setFeedbackIntro(false);
    // Back to where the person was: the control that opened it, or the page when it's gone.
    window.requestAnimationFrame(() => {
      const target = feedbackReturn.current;
      // The person keeps their place on the page (QA-032, QA-060), unless the control is outside
      // the window.
      if (target?.isConnected === true) returnFocus(target);
      else document.getElementById('main')?.focus({ preventScroll: true });
      feedbackReturn.current = null;
    });
  }, []);
  const feedbackOffNow = useCallback(() => {
    setRefusedOffer(feedback);
    // The control that opened it is going; focus lands on the page.
    feedbackReturn.current = null;
    setFeedbackOpen(false);
    setFeedbackIntro(false);
    onFeedbackOff?.();
  }, [feedback, onFeedbackOff]);
  const feedbackArea =
    (current === undefined ? null : LEVEL_ONE.find((item) => item.id === current)?.label) ??
    'MyRewards';

  return (
    <div className={s.shell}>
      <a className={s.skip} href="#main">
        Skip to main content
      </a>
      <SetupSandboxBanner />
      {preview && onExitPreview && (
        <PreviewBanner
          label={preview.label}
          {...(preview.signedInAs !== undefined && { signedInAs: preview.signedInAs })}
          onExit={onExitPreview}
          {...(onPreviewOpen && canPreview && { onChange: onPreviewOpen })}
        />
      )}
      <AppHeader
        me={me}
        nav={nav}
        {...(navHrefs && { navHrefs })}
        {...(current !== undefined && { current })}
        bell={bell}
        canPreview={canPreview}
        helpOpen={helpOpen}
        onHelpToggle={() => {
          if (helpOpen) closeHelp(helpButtonRef.current);
          else openHelp();
        }}
        onHelpOpen={openHelp}
        searchOpen={searchOpen}
        onSearchOpen={() => {
          setSearchOpen(true);
        }}
        onSignOut={onSignOut}
        helpButtonRef={helpButtonRef}
        {...(feedbackOn && { onFeedbackOpen: openFeedback })}
        {...(settingsHref !== undefined && { settingsHref })}
        {...(onPreviewOpen !== undefined && { onPreviewOpen })}
        {...(homeHref !== undefined && { homeHref })}
      />
      <div className={s.body}>
        <main id="main" tabIndex={-1} className={s.main}>
          {/* A page's own Send feedback (APP-138) opens this same flow; none while it's off. */}
          <FeedbackOpenerProvider value={feedbackOn ? openFeedback : null}>
            {children}
          </FeedbackOpenerProvider>
        </main>
        {helpOpen && (
          <HelpPanel
            page={help}
            topics={helpTopics}
            {...(helpSource && { source: helpSource })}
            aiHelpEnabled={aiHelpEnabled}
            {...(helpChat !== undefined && { chat: helpChat })}
            {...(helpContact && { contact: helpContact })}
            {...(feedbackOn && {
              onFeedback: () => {
                openFeedback(helpReturn());
              },
            })}
            onShowMe={showMe}
            onClose={() => {
              closeHelp();
            }}
          />
        )}
      </div>
      <p className="visually-hidden" role="status">
        {shownNote}
      </p>
      <AppFooter
        photoStrip={footerPhotoStrip}
        onHelp={openHelp}
        {...(awardsPolicyHref !== undefined && { awardsPolicyHref })}
      />
      {feedbackIntro && feedbackOn && (
        <VoiceIntro
          onStart={() => {
            leaveIntro(false);
          }}
          onType={() => {
            leaveIntro(true);
          }}
          // Not now: nothing was started, and focus goes back to where they were.
          onCancel={closeFeedback}
        />
      )}
      {feedback !== null && feedbackOpen && feedbackOn && (
        <FeedbackFlow
          features={feedback}
          area={feedbackArea}
          hero={hero}
          startTyping={feedbackTyping}
          onClose={closeFeedback}
          onOff={feedbackOffNow}
          onSessionEnded={() => {
            onFeedbackSessionEnded?.();
          }}
          onVoiceUsed={() => {
            onFeedbackVoiceUsed?.();
          }}
          {...(onFeedbackSent !== undefined && { onSent: onFeedbackSent })}
        />
      )}
      <SearchPalette
        isOpen={searchOpen}
        onOpenChange={setSearchOpen}
        search={search}
        {...(searchJumpTo && { jumpTo: searchJumpTo })}
        {...(searchRecent && { recent: searchRecent })}
      />
    </div>
  );
}

/**
 * The page body under the hero: max 1320 px wide with 32 px gutters. `nav` puts a section's
 * sidebar beside it (NAV2: the queue's views), stacked above it below 900 px. `width="full"` lifts
 * the 1,320 px limit for a page that uses the whole window (APP-130 D96: Letters), keeping the
 * gutters; every other page stays contained.
 */
export function PageBody({
  children,
  nav,
  width = 'contained',
}: {
  children: ReactNode;
  nav?: ReactNode;
  width?: 'contained' | 'full';
}): ReactElement {
  const page = cx(s.page, width === 'full' && s.pageFull);
  if (nav === undefined) return <div className={page}>{children}</div>;
  return (
    <div className={cx(page, s.withNav)}>
      {nav}
      <div className={s.navMain}>{children}</div>
    </div>
  );
}

/** Two wrapping columns: a flexible main column and a side column of about 340 px. */
export function TwoColumns({
  main,
  side,
  sideLabel = 'More on this page',
  wideMain = false,
}: {
  main: ReactNode;
  side: ReactNode;
  /** Names the side column's landmark; give each one on a page its own name. */
  sideLabel?: string;
  /**
   * The main column holds a table that needs about 840 px: the side column moves under it
   * rather than squeezing it (APP-094 review).
   */
  wideMain?: boolean;
}): ReactElement {
  return (
    <div className={cx(s.columns, wideMain && s.wideMain)}>
      <div className={s.mainColumn}>{main}</div>
      <aside className={s.sideColumn} aria-label={sideLabel}>
        {side}
      </aside>
    </div>
  );
}

/** A responsive grid of tiles (KPI rows). */
export function TileGrid({ children }: { children: ReactNode }): ReactElement {
  return <div className={s.grid}>{children}</div>;
}
